import { enqueueSnackbar } from 'notistack';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import {
  clearSession,
  configureSessionTimeout,
  lastActivity,
  markActivity,
  saveSession,
  sessionIsIdle,
  sessionTimeout
} from './adminSession';
const AuthContext = createContext(null);
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionEnding, setSessionEnding] = useState(false);
  const navigate = useNavigate();
  const lastRecorded = useRef(0);
  configureSessionTimeout(Number(import.meta.env.VITE_ADMIN_SESSION_TIMEOUT));
  const logout = () => {
    clearSession();
    setAdmin(null);
    setSessionEnding(false);
    navigate('/login');
  };
  useEffect(() => {
    try {
      const token = localStorage.getItem('adminToken');
      const user = JSON.parse(localStorage.getItem('adminData') || 'null');
      if (token && user?.role === 'admin' && !sessionIsIdle()) {
        if (!lastActivity()) markActivity();
        setAdmin(user);
      } else clearSession();
    } catch {
      clearSession();
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (!admin) return undefined;
    const activity = () => {
      const now = Date.now();
      if (now - lastRecorded.current > 1000) {
        markActivity(now);
        lastRecorded.current = now;
        setSessionEnding(false);
      }
    };
    const expire = () => {
      logout();
      enqueueSnackbar(
        'Session inactive. Reconnectez-vous pour continuer ; vos brouillons sont conservés.',
        { variant: 'warning' }
      );
    };
    const check = () => {
      if (sessionIsIdle()) expire();
      else
        setSessionEnding(
          Date.now() - lastActivity() >= sessionTimeout() - 60000
        );
    };
    const timer = window.setInterval(check, 1000);
    const otherTab = event => {
      if (event.key === 'adminToken' && !event.newValue) logout();
    };
    for (const event of ['pointerdown', 'keydown', 'scroll'])
      window.addEventListener(event, activity, { passive: true });
    window.addEventListener('zenoo-admin-session-expired', expire);
    window.addEventListener('storage', otherTab);
    return () => {
      window.clearInterval(timer);
      for (const event of ['pointerdown', 'keydown', 'scroll'])
        window.removeEventListener(event, activity);
      window.removeEventListener('zenoo-admin-session-expired', expire);
      window.removeEventListener('storage', otherTab);
    };
  }, [admin]);
  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { user, token, refreshToken } = response.data.data;
      if (user?.role !== 'admin')
        throw new Error(
          'Cet espace est réservé aux administrateurs. L’accès modérateur n’est pas encore activé.'
        );
      saveSession(user, token, refreshToken);
      setAdmin(user);
      setSessionEnding(false);
      navigate('/dashboard');
      return { success: true };
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || 'Erreur de connexion';
      return { success: false, error: message };
    }
  };
  return (
    <AuthContext.Provider
      value={{
        admin,
        loading,
        login,
        logout,
        sessionEnding,
        isAuthenticated: admin?.role === 'admin'
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
